import { format, isValid, parse } from 'date-fns'
import { DATE_FORMATS } from './date'

/**
 * Shared masking + parsing for typed MM/DD/YYYY date inputs.
 * Extracted from the table-cell DateField editor so every typed date input
 * (inline field editors, DatePickerPanel) behaves identically.
 */

/**
 * Progressively mask raw input into MM/DD/YYYY as the user types.
 * Non-digits are stripped; slashes are inserted after month and day.
 */
export function maskDateInput(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`
}

/**
 * Parse a masked date string leniently.
 * Accepts MM/DD (current year), MM/DD/YY (expanded against the current
 * century, e.g. "26" → 2026), or MM/DD/YYYY. Returns null for anything else
 * or for out-of-range parts (month 1-12, day 1-31, year 1900-2100).
 */
export function parseMaskedDate(formattedValue: string): Date | null {
  let normalized = formattedValue
  const currentYear = new Date().getFullYear()
  if (formattedValue.length === 5) {
    normalized = `${formattedValue}/${currentYear}`
  } else if (formattedValue.length === 8) {
    const twoDigitYear = parseInt(formattedValue.slice(6, 8))
    if (Number.isNaN(twoDigitYear)) return null
    const century = Math.floor(currentYear / 100) * 100
    normalized = `${formattedValue.slice(0, 6)}${century + twoDigitYear}`
  } else if (formattedValue.length !== 10) {
    return null
  }

  try {
    const date = parse(normalized, DATE_FORMATS.input, new Date())
    if (!isValid(date)) return null

    const month = parseInt(normalized.slice(0, 2))
    const day = parseInt(normalized.slice(3, 5))
    const year = parseInt(normalized.slice(6, 10))

    if (month < 1 || month > 12) return null
    if (day < 1 || day > 31) return null
    if (year < 1900 || year > 2100) return null

    return date
  } catch {
    return null
  }
}

/** Format a Date for a masked input's text value. */
export function formatMaskedDate(date: Date): string {
  return format(date, DATE_FORMATS.input)
}

/**
 * Where the caret should land after masking inserts slashes.
 * `rawCursor` is the caret position in the raw (pre-mask) value.
 */
export function getMaskedCursorPosition(
  oldValue: string,
  rawValue: string,
  maskedValue: string,
  rawCursor: number,
): number {
  let newPosition = rawCursor

  const lengthDiff = maskedValue.length - rawValue.length
  if (lengthDiff > 0) {
    if (rawCursor === 2 || rawCursor === 5) {
      newPosition = rawCursor + 1
    } else if (rawCursor > 2 && oldValue.length < 3) {
      newPosition = rawCursor + 1
    } else if (rawCursor > 5 && oldValue.length < 6) {
      newPosition = rawCursor + 1
    }
  }

  return newPosition
}
